import { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { db, functions } from '../../firebase'
import { formatDateAr, paginate } from './subscriptionHelpers'

type PaymentRequestDoc = {
  id: string
  sellerName?: string
  sellerPhone?: string
  payerPhone?: string
  amountEgp?: number
  status?: string
  verificationStatus?: string
  couponCode?: string | null
  createdAt?: any
}

const STATUS_FILTERS = [
  { value: 'all', label: 'كل الحالات' },
  { value: 'awaiting_payment', label: 'بانتظار التحويل' },
  { value: 'confirmed', label: 'مؤكد' },
  { value: 'cancelled', label: 'ملغي' },
]

function statusLabel(status?: string) {
  if (status === 'awaiting_payment') return { label: 'بانتظار التحويل', color: 'warning' as const }
  if (status === 'confirmed') return { label: 'مؤكد', color: 'success' as const }
  if (status === 'cancelled') return { label: 'ملغي', color: 'default' as const }
  return { label: status || '—', color: 'default' as const }
}

export default function SubscriptionPaymentsPage() {
  const [payments, setPayments] = useState<PaymentRequestDoc[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  })

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'subscription_payment_requests'), (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as PaymentRequestDoc[]
      rows.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
      setPayments(rows)
    })
    return () => unsub()
  }, [])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return payments.filter((row) => {
      if (status !== 'all' && row.status !== status) return false
      if (!q) return true
      return (
        row.sellerName?.toLowerCase().includes(q) ||
        row.sellerPhone?.includes(q) ||
        row.payerPhone?.includes(q) ||
        row.couponCode?.toLowerCase().includes(q) ||
        false
      )
    })
  }, [payments, searchQuery, status])

  useEffect(() => {
    setPage(0)
    setSelectedIds([])
  }, [searchQuery, status])

  const pageRows = paginate(filtered, page, rowsPerPage)
  const selectedOnPage = pageRows.filter((row) => selectedIds.includes(row.id))
  const awaitingSelected = selectedIds.filter((id) =>
    payments.some((row) => row.id === id && row.status === 'awaiting_payment'),
  )

  const notify = (message: string, severity: typeof snackbar.severity) =>
    setSnackbar({ open: true, message, severity })

  const confirmPayment = async (id: string) => {
    const call = httpsCallable(functions, 'adminConfirmSubscriptionPayment')
    await call({ requestId: id })
  }

  const confirmSelected = async () => {
    if (!awaitingSelected.length) return
    setBusy(true)
    try {
      for (const id of awaitingSelected) {
        await confirmPayment(id)
      }
      notify(`تم تأكيد ${awaitingSelected.length} طلب`, 'success')
      setSelectedIds([])
    } catch {
      notify('تعذر تأكيد بعض الطلبات', 'error')
    } finally {
      setBusy(false)
    }
  }

  const toggleAllPage = (checked: boolean) => {
    if (checked) {
      setSelectedIds(Array.from(new Set([...selectedIds, ...pageRows.map((row) => row.id)])))
    } else {
      const pageIds = new Set(pageRows.map((row) => row.id))
      setSelectedIds(selectedIds.filter((id) => !pageIds.has(id)))
    }
  }

  return (
    <Stack spacing={2.5}>
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            placeholder="بحث بالتاجر أو رقم التحويل أو الكوبون"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>الحالة</InputLabel>
            <Select label="الحالة" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_FILTERS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {selectedIds.length > 0 && (
        <Paper sx={{ p: 1.5, bgcolor: 'rgba(15,118,110,0.06)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography sx={{ flex: 1, fontWeight: 600 }}>
              تم تحديد {selectedIds.length} سجل
              {awaitingSelected.length ? ` — منها ${awaitingSelected.length} بانتظار التحويل` : ''}
            </Typography>
            <Button
              size="small"
              variant="contained"
              disabled={!awaitingSelected.length || busy}
              onClick={confirmSelected}
            >
              تأكيد المحددين
            </Button>
            <Button size="small" onClick={() => setSelectedIds([])}>
              إلغاء التحديد
            </Button>
          </Stack>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={pageRows.length > 0 && selectedOnPage.length === pageRows.length}
                  indeterminate={selectedOnPage.length > 0 && selectedOnPage.length < pageRows.length}
                  onChange={(e) => toggleAllPage(e.target.checked)}
                />
              </TableCell>
              <TableCell>التاجر</TableCell>
              <TableCell>من رقم</TableCell>
              <TableCell>المبلغ</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>التاريخ</TableCell>
              <TableCell>إجراء</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => {
              const chip = statusLabel(row.status)
              return (
                <TableRow key={row.id} hover selected={selectedIds.includes(row.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onChange={() =>
                        setSelectedIds((prev) =>
                          prev.includes(row.id)
                            ? prev.filter((id) => id !== row.id)
                            : [...prev, row.id],
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{row.sellerName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.sellerPhone}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ direction: 'ltr', textAlign: 'right' }}>{row.payerPhone || '—'}</TableCell>
                  <TableCell>
                    {row.amountEgp ?? 0} جنيه
                    {row.couponCode ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.couponCode}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={chip.label} color={chip.color} />
                  </TableCell>
                  <TableCell>{formatDateAr(row.createdAt)}</TableCell>
                  <TableCell>
                    {row.status === 'awaiting_payment' && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true)
                          try {
                            await confirmPayment(row.id)
                            notify('تم تأكيد الدفع يدوياً', 'success')
                          } catch {
                            notify('تعذر تأكيد الدفع', 'error')
                          } finally {
                            setBusy(false)
                          }
                        }}
                      >
                        تأكيد يدوي
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">لا توجد طلبات مطابقة</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="عدد الصفوف"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} من ${count}`}
        />
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
